import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    TablePagination,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip as MuiTooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { Download, DragIndicator, Image, PictureAsPdf, TableChart } from '@mui/icons-material';
import { Alert, DialogV2 } from 'c-components';
import {
    Bar,
    BarChart,
    Brush,
    Cell,
    DefaultTooltipContent,
    LabelList,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { useCommonTranslation } from 'c-translation';
import { useDateUtils } from 'c-hooks';
import { parseISO } from 'date-fns';
import type {
    DashboardAggregation,
    DashboardFilter,
    DashboardWidgetConfig,
    DashboardChartControls,
    DashboardViewFilter,
} from './dashboardConfig';
import {
    DASHBOARD_DEFAULT_PAGE_SIZE,
    DASHBOARD_MAX_LIMIT,
    humanizeField,
    comparisonColorDefaults,
    expectedMetricOf,
    isAudienceField,
    isExpectedField,
    resolveWidgetTitle,
    seedChartControls,
    globalFiltersForWidget,
    toQueryFilters,
    translateWidgetTitle,
} from './dashboardConfig';
import type { DashboardCalculatedField } from './dashboardCalculations';
import {
    calcIdOf,
    isCalcField,
    planWidgetQuery,
    shapeWidgetRows,
    totalOfRows,
} from './dashboardCalculations';
import {
    EXPORT_IGNORE_ATTR,
    downloadCsv,
    downloadElementImage,
    exportFileName,
} from './dashboardExport';
import type { DashboardTableColumn } from './dashboardTableData';
import {
    dimensionColumn,
    formatFullNumber,
    formatRatioPercent,
    numberColumn,
    tableToCsvRows,
} from './dashboardTableData';
import DashboardDataTable from './DashboardDataTable';
import { campaignLinkOf } from './dashboardCampaignLink';
import useOpenDashboardCampaign from './useOpenDashboardCampaign';
import DashboardStatBody, { DashboardStatIconTile } from './DashboardStatBody';
import { comparisonQueryFilters, percentChange } from './dashboardStat';
import { useRegisterWidgetExport } from './DashboardExportContext';
import type { DashboardReportRequest } from './useDashboardReport';
import { useDashboardReport } from './useDashboardReport';
import {
    applyLineControls,
    formatAxisCompact,
    formatCompact,
    splitLabelIntoLines,
} from './dashboardWidgetLib';
import {
    ACTUAL_KEY,
    DIM_PREFIX,
    EXPECTED_KEY,
    buildChartData,
    buildComboData,
    buildPivot,
    capChartData,
    capCombo,
    capPivot,
    maxVisibleOf,
    metricKey,
    slicePercentLabel,
    widgetChartMaths,
} from './dashboardChartData';

type Props = {
    config: DashboardWidgetConfig;
    // Page-level filters, each optionally scoped to one source. Only those that apply to
    // this widget's source are sent with its query.
    globalFilters?: DashboardFilter[];
    height?: number;
    // Share of its row this widget occupies, from the layout. The live chart toolbar only
    // appears at full width (or expanded), where there is room to read it.
    widthPct?: number;
    // When provided, render from these rows instead of fetching (used for the config preview).
    previewData?: any[];
    // The dashboard's calculated fields for this widget's source.
    calculatedFields?: DashboardCalculatedField[];
    // Fill the box the dashboard grid gives it, rather than being `height` tall.
    fill?: boolean;
    // The dashboard is being rearranged: the heading becomes the handle it is dragged by.
    editing?: boolean;
};

// The class react-grid-layout is told to drag widgets by.
export const DASHBOARD_DRAG_HANDLE_CLASS = 'dashboard-widget-drag-handle';

// The full breakdown shows a bar chart this many categories at a time.
const BREAKDOWN_PAGE_SIZE = 25;

// Every chart shows at most this many categories; the remainder is rolled into an
// "Others" entry, with a "View full breakdown" button revealing the complete set.
// A ranked comparison bar's two segments: up to the smaller figure, then on to the larger.
const CMP_BASE_KEY = '__cmpBase';
const CMP_EXTRA_KEY = '__cmpExtra';
const CMP_EXPECTED_LARGER_KEY = '__cmpExpectedLarger';

// A single value's figure, read off the totals row: a calculated measure worked out on the
// totals (a ratio of the sums, not a sum of each row's ratio), anything else added up as its
// aggregation says.
const statFigure = (
    total: Record<string, unknown>,
    metric: { field: string; aggregation: string } | undefined,
): number => (metric ? Number(total[metricKey(metric)] ?? NaN) : NaN);

// A single value has room for the figure itself up to a million (2,319 campaigns, not 2.3K);
// past that it is compact (53.10B).
const statNumber = (value: number, ratio: boolean): string => {
    if (ratio) return formatRatioPercent(value);
    return Math.abs(value) < 1_000_000 ? formatFullNumber(value) : formatCompact(value);
};

const prettyLabel = (raw: string) =>
    raw
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase())
        .trim();

const WrappedYAxisTick = ({ x, y, payload }: any) => {
    const lines = splitLabelIntoLines(String(payload?.value ?? ''));
    const lineHeight = 12;
    const startDy = -((lines.length - 1) / 2) * lineHeight + 4;
    return (
        <text x={x} y={y} textAnchor="end" fill="#666" fontSize={11}>
            {lines.map((line, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <tspan key={`${line}-${index}`} x={x} dy={index === 0 ? startDy : lineHeight}>
                    {line}
                </tspan>
            ))}
        </text>
    );
};

const DashboardWidget: React.FC<Props> = ({
    config,
    globalFilters = [],
    height = 300,
    widthPct = 100,
    previewData,
    calculatedFields,
    fill = false,
    editing = false,
}) => {
    const theme = useTheme();
    const t = useCommonTranslation();
    const { formatDateStringIgnoreTime } = useDateUtils();
    const [breakdownOpen, setBreakdownOpen] = useState(false);
    const calcs = useMemo(() => calculatedFields ?? [], [calculatedFields]);
    // A field's name on screen: a calculated field's own, otherwise the field's label.
    const labelOf = (field: string): string => {
        if (!isCalcField(field)) return humanizeField(field, t);
        const id = calcIdOf(field);
        const name = calcs.find(c => c.id === id)?.name;
        return name != null
            ? translateWidgetTitle(name, t)
            : t('Pages.DashboardHome.custom.missingCalc');
    };
    // What the widget asks the API for (its calculated fields' inputs instead of the fields
    // themselves) and how the rows that come back are worked into what it shows.
    const plan = useMemo(() => planWidgetQuery(config, calcs), [config, calcs]);

    // The chart data maths (trend, ranking direction, comparison) for this config.
    const maths = useMemo(() => widgetChartMaths(config), [config]);
    const { isTrend, expectedKey } = maths;
    const isLine = config.graphType === 'line';

    // Live chart controls. They start from the widget's saved defaults and are session-only —
    // changing one here never writes back to the config.
    const [controls, setControls] = useState<DashboardChartControls>(() =>
        seedChartControls(config),
    );
    useEffect(() => {
        setControls(seedChartControls(config));
        // Deliberately keyed on the saved defaults alone: depending on `config` would reset a
        // viewer's live toggles every time any unrelated part of the widget changed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        config.cumulative,
        config.showNumbers,
        config.showPercent,
        config.viewFilter,
        config.comparison,
        config.comparisonSeries,
    ]);
    const setControl = <K extends keyof DashboardChartControls>(
        key: K,
        value: DashboardChartControls[K],
    ) => setControls(current => ({ ...current, [key]: value }));
    // A calculated field formatted as a percentage holds a ratio (1.0984 reads 109.84%).
    const isRatioMetric = (metric: { field: string } | undefined) =>
        metric != null &&
        isCalcField(metric.field) &&
        calcs.find(c => c.id === calcIdOf(metric.field))?.format === 'percent';
    const firstIsRatio = isRatioMetric(config.metrics[0]);
    const fmt = (value: number) =>
        firstIsRatio ? formatRatioPercent(value) : formatCompact(value);

    // Sources don't share a schema, so only the global filters scoped to the source this
    // widget queries reach it. A widget can opt out of them altogether.
    const queryFilters = useMemo(
        () => [
            ...(config.ignoreGlobalFilters
                ? []
                : globalFiltersForWidget(globalFilters, config.datasourceId)),
            ...plan.filters,
        ],
        [config.ignoreGlobalFilters, config.datasourceId, globalFilters, plan],
    );

    // The exact request this widget needs; identical requests are de-duplicated by the
    // shared report cache so we never fire the same call twice.
    const request = useMemo<DashboardReportRequest | null>(() => {
        if (previewData) return null;
        return {
            // graphType is required by the API but ignored; the visual type is resolved
            // client-side, so always send a safe/known value here.
            graphType: 'bar',
            metrics: plan.metrics,
            dimensions: plan.dimensions,
            filters: toQueryFilters(queryFilters),
            sort: plan.sort,
            // Always fetch the maximum; the client caps the visible categories to top N.
            limit: DASHBOARD_MAX_LIMIT,
            // When bound to a datasource, route the query to its saved table.
            datasourceId: config.datasourceId,
        };
    }, [config, queryFilters, previewData, plan]);

    const { data, isLoading: fetchLoading } = useDashboardReport(request);
    // A table row about a campaign opens it. Looked up within the filters the widget ran with.
    const campaignQueryFilters = useMemo(() => toQueryFilters(queryFilters), [queryFilters]);
    const openCampaign = useOpenDashboardCampaign(campaignQueryFilters);
    const tableDimensionFields = config.dimensions.map(d => d.field);
    const campaignLinkOfRow = (row: Record<string, unknown>) =>
        campaignLinkOf(row, tableDimensionFields, config.datasourceId == null);
    const isLoading = previewData ? false : fetchLoading;

    const rows = useMemo<any[]>(
        () =>
            shapeWidgetRows(
                (previewData ?? (data as any)?.data ?? []) as Record<string, unknown>[],
                config,
                plan,
                calcs,
            ),
        [data, previewData, config, plan, calcs],
    );
    const palette = useMemo(
        () =>
            [
                theme.palette.primary?.main,
                theme.palette.secondary?.main,
                (theme.palette as any).tertiary?.main,
                (theme.palette as any).quaternary?.main,
                (theme.palette as any).quinary?.main,
                (theme.palette as any).senary?.main,
            ].filter((c): c is string => typeof c === 'string' && c.length > 0),
        [theme],
    );
    // Resolve a theme palette key ('primary'…) to its colour, falling back to a palette slot.
    const colorFor = (key: string | undefined, fallbackIndex = 0): string =>
        (key && (theme.palette as any)[key]?.main) || palette[fallbackIndex] || palette[0];
    const barColor = colorFor(config.color, 0);
    const colorDefaults = comparisonColorDefaults(config);
    const expectedColor = colorFor(config.expectedColor ?? colorDefaults.expected, 1);
    const actualColor = colorFor(config.actualColor ?? colorDefaults.actual, 0);
    const comparisonSeries = config.comparisonSeries ?? 'both';
    const expectedMetric = expectedMetricOf(config.metrics);
    const hasExpected = expectedMetric != null;
    const { showComparison } = maths;
    // A Geopath audience comparison reads as audited figures rather than an expectation.
    const audienceComparison = isAudienceField(expectedMetric?.field ?? '');
    const expectedName = audienceComparison ? humanizeField(expectedMetric.field, t) : 'Expected';

    // Raw multi-metric charts colour each metric from the palette in order, except that a
    // Geopath audience metric always takes the primary colour and the rest follow it.
    const metricColorOrder = useMemo(() => {
        const audienceFirst = [
            ...config.metrics.filter(m => isAudienceField(m.field)),
            ...config.metrics.filter(m => !isAudienceField(m.field)),
        ];
        return config.metrics.map(m => audienceFirst.indexOf(m));
    }, [config.metrics]);
    const metricColor = (index: number): string =>
        palette[(metricColorOrder[index] ?? index) % palette.length];
    // Which metric lines the view filter leaves visible. The original index rides along so a
    // series keeps its palette colour when the ones before it are filtered out.
    const visibleMetrics = useMemo(
        () =>
            config.metrics
                .map((metric, index) => ({ metric, index }))
                .filter(({ metric }) => {
                    const isExpected = isExpectedField(metric.field);
                    // Expected is the denominator in percent mode — plotting it would just
                    // draw a flat 100% line.
                    if (controls.showPercent) return !isExpected;
                    if (controls.viewFilter === 'expected') return isExpected;
                    if (controls.viewFilter === 'delivered') return !isExpected;
                    return true;
                }),
        [config.metrics, controls.viewFilter, controls.showPercent],
    );

    // Rows for a single-dimension line chart, with accumulate / percent applied.
    const lineData = (pts: Record<string, number | string>[]) =>
        applyLineControls(pts, config.metrics.map(metricKey), expectedKey, controls);

    const labelText = (value: number) =>
        controls.showPercent ? `${Math.round(Number(value))}%` : formatCompact(Number(value));

    // Percent mode plots a ratio, so the axis has to read as one too.
    const lineAxisTick = (value: number) =>
        controls.showPercent ? `${Math.round(Number(value))}%` : formatAxisCompact(Number(value));

    // Point labels thin out the same way the axis ticks do: a narrow card has room for a
    // handful, the expanded view for more. Labelling all 60 days of a series just fills the
    // chart with overlapping text.
    const renderPointLabels = (
        rows: Record<string, number | string>[],
        seriesKeys: string[],
        key: string,
        expanded: boolean,
    ) => {
        if (!controls.showNumbers) return null;
        const budget = expanded ? 14 : Math.max(3, Math.round(6 * (widthPct / 100)));
        const step = rows.length > budget ? Math.ceil(rows.length / budget) : 1;
        return (
            <LabelList
                position="top"
                offset={8}
                content={(props: any) => {
                    const { x, y, value, index } = props;
                    if (value == null || index % step !== 0) return null;
                    // Sit above the point when this series is the highest here, below when it
                    // is not, so two series crossing never stack their labels on each other.
                    const others = seriesKeys
                        .filter(k => k !== key)
                        .map(k => Number(rows[index]?.[k] ?? 0));
                    const above = others.length === 0 || Number(value) >= Math.max(...others);
                    return (
                        <text
                            x={x}
                            y={above ? y - 6 : y + 14}
                            textAnchor="middle"
                            fontSize={10}
                            fill={theme.palette.text.secondary}
                        >
                            {labelText(Number(value))}
                        </text>
                    );
                }}
            />
        );
    };

    // Full data with expected/actual per category, sorted by the chosen ranking basis.
    const chartData = useMemo(() => buildChartData(rows, config, maths), [rows, config, maths]);

    // Top N is capped at MAX_VISIBLE (5) regardless of any stored value.
    const maxVisible = maxVisibleOf(config.topN);

    // Whether the remainder past the cap is rolled into one "Others" entry. Opt-in: a
    // "Top 5" chart is about those five, and an Others bar usually dwarfs them.
    const showOthers = config.showOthers === true;

    // Bar value labels are printed past the end of the bar, so a narrow widget runs them off
    // the edge. Shrink with the width rather than clipping.
    const barLabelFontSize = widthPct >= 75 ? 10 : widthPct >= 50 ? 9 : 8;

    // Pie and donut can plot every category rather than the ranked N, so the circle really is
    // all of the data; and can label each slice with its share of what is plotted, which adds
    // up to 100% whichever of the two is on.
    const showAllSlices = config.showAllSlices === true;
    const percentOfTotal = config.percentOfTotal === true;
    const sliceRows = (rows: Record<string, number | string>[]) =>
        showAllSlices ? chartData : rows;
    const sliceLabel = (value: number, total: number) =>
        slicePercentLabel(value, total, percentOfTotal) ?? fmt(value);

    // Top N, plus an aggregated "Others" bucket when asked for, its calculated measures worked
    // out on its own totals.
    const cappedData = useMemo(
        () =>
            capChartData(chartData, config, maths, maxVisible, showOthers, points =>
                totalOfRows(points, plan.metrics, calcs, plan.calcIds),
            ),
        [chartData, config, maths, maxVisible, showOthers, plan, calcs],
    );

    // Second-dimension support: for bar / horizontalBar / line, a 2nd dimension splits
    // each category (dim 1) into a series per distinct dim-2 value. Value per cell is the
    // primary metric. Comparison mode keeps its own dedicated path.
    const has2Dims = config.dimensions.length >= 2;
    // Bars (with or without axis) show one bar per "dimension A - dimension B" combo,
    // ranked and capped to top N, labelled "A - B" (single colour — the label carries both
    // values, so no colour coding is needed). A line instead splits into a coloured series
    // per dimension-2 value. Both take priority over Expected-vs-Actual comparison.
    const isComboBar =
        has2Dims &&
        (config.graphType === 'bar' ||
            config.graphType === 'horizontalBar' ||
            config.graphType === 'rankedBar');
    const isSeriesLine = has2Dims && config.graphType === 'line';

    const pivot = useMemo(
        () => (isSeriesLine ? buildPivot(rows, config, maths) : null),
        [isSeriesLine, rows, config, maths],
    );

    const comboData = useMemo(
        () => (isComboBar ? buildComboData(rows, config, maths) : null),
        [isComboBar, rows, config, maths],
    );

    // How many entries the chart actually has (combos / line categories / single-dim rows)
    // and whether that exceeds the visible cap — drives the "View full breakdown" button.
    const fullCount = comboData?.length ?? pivot?.points.length ?? chartData.length;
    const hasMore = fullCount > maxVisible;

    const displayTitle = useMemo(
        () => resolveWidgetTitle(config, t, false, calcs),
        [config, t, calcs],
    );
    const breakdownTitle = useMemo(
        () => resolveWidgetTitle(config, t, true, calcs),
        [config, t, calcs],
    );
    const valueOf = (point: Record<string, number | string>) =>
        Number(point[config.metrics[0] ? metricKey(config.metrics[0]) : ''] ?? 0);

    const renderChart = (
        points: Record<string, number | string>[],
        chartHeight: number,
        expanded = false,
        // The breakdown's current page of a two-dimension bar chart's combos.
        comboRange?: [number, number],
    ) => {
        // Bars use the combo list; lines use the series pivot. Both cap to top N + Others
        // for the card, and show every entry when expanded (the breakdown dialog).
        const comboPoints =
            comboRange && comboData
                ? comboData.slice(comboRange[0], comboRange[1])
                : capCombo(comboData, expanded, maxVisible, showOthers);
        const pivotView = capPivot(pivot, expanded, maxVisible, showOthers);

        // Category x-axis (line / vertical bar): tilt the labels so more fit, and add a
        // scrollable brush when expanded so every name can be reached.
        const BRUSH_WINDOW = 12;
        // A trend plots every point, so labelling all of them is illegible. Thin the ticks to
        // roughly this many; the tooltip and the expanded brush still reach every point.
        const TREND_LABELS = expanded ? 14 : 7;
        const renderCategoryXAxis = (count = 0) => (
            <XAxis
                dataKey="name"
                // Smaller text when not expanded so labels aren't clipped by the card
                // bottom; more axis height when expanded so they clear the brush.
                tick={{ fontSize: expanded ? 11 : 9 }}
                angle={-30}
                textAnchor="end"
                height={expanded ? 104 : 64}
                // 0 labels every point — right for a capped category axis, unreadable for a
                // date series, which skips whole ticks between labels instead.
                interval={isTrend && count > TREND_LABELS ? Math.ceil(count / TREND_LABELS) - 1 : 0}
            />
        );
        const renderCategoryBrush = (count: number) =>
            expanded && count > BRUSH_WINDOW ? (
                <Brush
                    dataKey="name"
                    height={20}
                    travellerWidth={8}
                    stroke={barColor}
                    startIndex={0}
                    endIndex={BRUSH_WINDOW - 1}
                />
            ) : null;
        // Expected vs Actual comparison (only when the widget has an expected metric).
        //  • rankedBar → one bar per category, Expected + Actual stacked as two colours.
        //  • bar / horizontalBar → per-dimension, Expected and Actual as two coloured bars.
        if (
            showComparison &&
            hasExpected &&
            !isComboBar &&
            !isSeriesLine &&
            config.graphType !== 'deliveryComparison'
        ) {
            // A line has the live toolbar, whose series toggle starts from the saved "Show"
            // choice and then follows the viewer; the other charts keep the saved choice.
            const followsToolbar = config.graphType === 'line';
            const showExpected = followsToolbar
                ? controls.viewFilter !== 'delivered'
                : comparisonSeries !== 'actual';
            const showActual = followsToolbar
                ? controls.viewFilter !== 'expected'
                : comparisonSeries !== 'expected';

            if (config.graphType === 'rankedBar') {
                // One bar per category that ends at the larger of the two figures: the smaller
                // one fills it up to its own value and the larger one carries on past it. The
                // two are never added together — that sum isn't a figure anything delivered.
                const both = showExpected && showActual;
                // Or, when the widget asks for it, the two as separate bars side by side.
                const sideBySide = both && config.comparisonSideBySide === true;
                const cmpPoints: Record<string, number | string>[] = points.map(p => {
                    const expected = Number(p[EXPECTED_KEY] ?? 0);
                    const actual = Number(p[ACTUAL_KEY] ?? 0);
                    if (!both) {
                        return { ...p, [CMP_BASE_KEY]: showExpected ? expected : actual };
                    }
                    return {
                        ...p,
                        [CMP_BASE_KEY]: Math.min(expected, actual),
                        [CMP_EXTRA_KEY]: Math.abs(expected - actual),
                        [CMP_EXPECTED_LARGER_KEY]: expected > actual ? 1 : 0,
                    };
                });
                const baseColorOf = (p: Record<string, number | string>) => {
                    if (!both) return showExpected ? expectedColor : actualColor;
                    return p[CMP_EXPECTED_LARGER_KEY] ? actualColor : expectedColor;
                };
                const extraColorOf = (p: Record<string, number | string>) =>
                    p[CMP_EXPECTED_LARGER_KEY] ? expectedColor : actualColor;
                // The bar's segments aren't the figures themselves, so the tooltip reads the
                // real values off the row — through Recharts' own tooltip, so it looks like every
                // other chart's.
                const renderCmpTooltip = (tProps: any) => {
                    const row = tProps?.payload?.[0]?.payload;
                    if (!tProps?.active || !row) return null;
                    const payload = [
                        ...(showExpected
                            ? [
                                  {
                                      name: expectedName,
                                      value: Number(row[EXPECTED_KEY] ?? 0),
                                      color: expectedColor,
                                  },
                              ]
                            : []),
                        ...(showActual
                            ? [
                                  {
                                      name: 'Actual',
                                      value: Number(row[ACTUAL_KEY] ?? 0),
                                      color: actualColor,
                                  },
                              ]
                            : []),
                    ];
                    return (
                        <DefaultTooltipContent
                            {...tProps}
                            payload={payload}
                            formatter={(value: number) => formatCompact(value)}
                        />
                    );
                };
                const legendPayload = [
                    ...(showExpected
                        ? [{ value: expectedName, type: 'square', color: expectedColor }]
                        : []),
                    ...(showActual
                        ? [{ value: 'Actual', type: 'square', color: actualColor }]
                        : []),
                ] as any[];
                return (
                    <ResponsiveContainer width="100%" height={chartHeight}>
                        <BarChart
                            layout="vertical"
                            data={cmpPoints}
                            margin={{ top: 4, right: 48, left: 8, bottom: 4 }}
                            barCategoryGap={8}
                        >
                            <XAxis type="number" hide domain={[0, (max: number) => max * 1.25]} />
                            <YAxis
                                type="category"
                                dataKey="name"
                                width={130}
                                tick={<WrappedYAxisTick />}
                                tickLine={false}
                                axisLine={false}
                            />
                            <Tooltip
                                content={renderCmpTooltip}
                                cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                            />
                            <Legend payload={legendPayload} />
                            {sideBySide && (
                                <Bar
                                    dataKey={EXPECTED_KEY}
                                    fill={expectedColor}
                                    barSize={10}
                                    radius={[0, 4, 4, 0]}
                                    isAnimationActive={false}
                                />
                            )}
                            {sideBySide && (
                                <Bar
                                    dataKey={ACTUAL_KEY}
                                    fill={actualColor}
                                    barSize={10}
                                    radius={[0, 4, 4, 0]}
                                    isAnimationActive={false}
                                />
                            )}
                            {!sideBySide && (
                                <Bar
                                    dataKey={CMP_BASE_KEY}
                                    stackId="cmp"
                                    barSize={16}
                                    radius={both ? undefined : [0, 4, 4, 0]}
                                    isAnimationActive={false}
                                >
                                    {cmpPoints.map(p => (
                                        <Cell key={String(p.name)} fill={baseColorOf(p)} />
                                    ))}
                                </Bar>
                            )}
                            {both && !sideBySide && (
                                <Bar
                                    dataKey={CMP_EXTRA_KEY}
                                    stackId="cmp"
                                    barSize={16}
                                    radius={[0, 4, 4, 0]}
                                    isAnimationActive={false}
                                >
                                    {cmpPoints.map(p => (
                                        <Cell key={String(p.name)} fill={extraColorOf(p)} />
                                    ))}
                                </Bar>
                            )}
                        </BarChart>
                    </ResponsiveContainer>
                );
            }

            // line — Expected and Actual as two series over the dimension, so a trend can
            // compare the two without becoming a bar chart.
            if (config.graphType === 'line') {
                const cmpRows = applyLineControls(
                    points,
                    [EXPECTED_KEY, ACTUAL_KEY],
                    EXPECTED_KEY,
                    controls,
                );
                const cmpKeys = [
                    ...(showExpected && !controls.showPercent ? [EXPECTED_KEY] : []),
                    ...(showActual ? [ACTUAL_KEY] : []),
                ];
                return (
                    <ResponsiveContainer width="100%" height={chartHeight}>
                        <LineChart
                            data={cmpRows}
                            margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                        >
                            {renderCategoryXAxis(points.length)}
                            <YAxis
                                tickFormatter={lineAxisTick}
                                width={64}
                                tick={{ fontSize: 11 }}
                            />
                            <Tooltip formatter={(value: number) => formatCompact(value)} />
                            <Legend />
                            {renderCategoryBrush(points.length)}
                            {showExpected && !controls.showPercent && (
                                <Line
                                    dataKey={EXPECTED_KEY}
                                    name={expectedName}
                                    stroke={expectedColor}
                                    strokeWidth={2}
                                    dot={false}
                                >
                                    {renderPointLabels(cmpRows, cmpKeys, EXPECTED_KEY, expanded)}
                                </Line>
                            )}
                            {showActual && (
                                <Line
                                    dataKey={ACTUAL_KEY}
                                    name={
                                        controls.showPercent
                                            ? `Actual (% of ${expectedName.toLowerCase()})`
                                            : 'Actual'
                                    }
                                    stroke={actualColor}
                                    strokeWidth={2}
                                    dot={false}
                                >
                                    {renderPointLabels(cmpRows, cmpKeys, ACTUAL_KEY, expanded)}
                                </Line>
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                );
            }

            // bar (vertical) / horizontalBar — per-dimension, two coloured bars per category.
            const horizontal = config.graphType === 'horizontalBar';
            return (
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart
                        layout={horizontal ? 'vertical' : 'horizontal'}
                        data={points}
                        margin={{
                            top: 8,
                            right: 32,
                            left: horizontal ? 24 : 8,
                            bottom: 8,
                        }}
                    >
                        {horizontal ? (
                            <>
                                <XAxis
                                    type="number"
                                    tickFormatter={formatAxisCompact}
                                    tick={{ fontSize: 11 }}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={130}
                                    tick={{ fontSize: 11 }}
                                />
                            </>
                        ) : (
                            <>
                                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                <YAxis
                                    tickFormatter={formatAxisCompact}
                                    width={64}
                                    tick={{ fontSize: 11 }}
                                />
                            </>
                        )}
                        <Tooltip formatter={(value: number) => formatCompact(value)} />
                        <Legend />
                        {showExpected && (
                            <Bar
                                dataKey={EXPECTED_KEY}
                                name={expectedName}
                                fill={expectedColor}
                                radius={[4, 4, 4, 4]}
                            />
                        )}
                        {showActual && (
                            <Bar
                                dataKey={ACTUAL_KEY}
                                name="Actual"
                                fill={actualColor}
                                radius={[4, 4, 4, 4]}
                            />
                        )}
                    </BarChart>
                </ResponsiveContainer>
            );
        }

        if (config.graphType === 'deliveryComparison') {
            // Two aggregated bars — "Expected" vs "Total" (Total stacked from the
            // received breakdown) — regardless of the dimension.
            const receivedMetrics = config.metrics.filter(m => /received/i.test(m.field));
            const seriesMetrics = receivedMetrics.length
                ? receivedMetrics
                : config.metrics.filter(m => !isExpectedField(m.field));
            const sumRows = (key: string) => rows.reduce((s, r) => s + Number(r[key] ?? 0), 0);
            const firstKey = seriesMetrics[0] ? metricKey(seriesMetrics[0]) : 'value';
            const expectedTotal = expectedMetric ? sumRows(metricKey(expectedMetric)) : 0;
            const expectedRow: Record<string, number | string> = {
                name: isAudienceField(expectedMetric?.field ?? '')
                    ? expectedName
                    : 'Expected Impacts',
            };
            const totalRow: Record<string, number | string> = { name: 'Total Impacts' };
            seriesMetrics.forEach(m => {
                const k = metricKey(m);
                expectedRow[k] = k === firstKey ? expectedTotal : 0;
                totalRow[k] = sumRows(k);
            });
            const seriesName = (field: string) =>
                prettyLabel(field.replace(/^(impacts|plays|time|audience)_received_/i, ''));
            return (
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart
                        layout="vertical"
                        data={[expectedRow, totalRow]}
                        margin={{
                            top: 10,
                            right: 30,
                            left: 8,
                            bottom: 10,
                        }}
                        barCategoryGap={28}
                    >
                        <XAxis
                            type="number"
                            tickFormatter={formatAxisCompact}
                            tick={{ fontSize: 11 }}
                        />
                        <YAxis
                            type="category"
                            dataKey="name"
                            width={110}
                            tick={{ fontSize: 13, fill: '#666' }}
                            tickLine={false}
                            axisLine={false}
                        />
                        <Tooltip
                            formatter={(value: number) => fmt(value)}
                            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                        />
                        <Legend />
                        {seriesMetrics.map((m, index) => (
                            <Bar
                                key={metricKey(m)}
                                dataKey={metricKey(m)}
                                stackId="a"
                                name={seriesName(m.field)}
                                fill={palette[index % palette.length]}
                                radius={[6, 6, 6, 6]}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            );
        }

        if (config.graphType === 'donut') {
            const pieData = sliceRows(points).map(row => ({
                name: String(row.name),
                value: valueOf(row),
            }));
            const total = pieData.reduce((acc, entry) => acc + entry.value, 0);
            return (
                <Stack direction="row" height={chartHeight} alignItems="center">
                    <Box sx={{ position: 'relative', width: '48%', height: '100%' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Tooltip formatter={(value: number) => sliceLabel(value, total)} />
                                <Pie
                                    data={pieData}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius="62%"
                                    outerRadius="92%"
                                    paddingAngle={1}
                                    isAnimationActive={false}
                                >
                                    {pieData.map((entry, index) => (
                                        <Cell
                                            key={entry.name}
                                            fill={palette[index % palette.length]}
                                        />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                pointerEvents: 'none',
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {fmt(total)}
                            </Typography>
                        </Box>
                    </Box>
                    <Stack
                        spacing={1.5}
                        sx={{ width: '52%', pl: 2, maxHeight: '100%', overflowY: 'auto' }}
                    >
                        {pieData.map((entry, index) => (
                            <Stack key={entry.name} direction="row" alignItems="center" spacing={2}>
                                <Box
                                    sx={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: '50%',
                                        flexShrink: 0,
                                        bgcolor: palette[index % palette.length],
                                    }}
                                />
                                <Typography
                                    variant="body1"
                                    sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}
                                >
                                    {entry.name}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 600, width: 72, textAlign: 'right' }}
                                >
                                    {fmt(entry.value)}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            );
        }

        if (config.graphType === 'pie') {
            const pieData = sliceRows(points).map(row => ({
                name: String(row.name),
                value: valueOf(row),
            }));
            const pieTotal = pieData.reduce((acc, entry) => acc + entry.value, 0);
            return (
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <PieChart>
                        <Tooltip formatter={(value: number) => sliceLabel(value, pieTotal)} />
                        <Legend />
                        <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            outerRadius="85%"
                            isAnimationActive={false}
                            label={
                                percentOfTotal
                                    ? ({ value }: any) => sliceLabel(Number(value), pieTotal)
                                    : undefined
                            }
                        >
                            {pieData.map((entry, index) => (
                                <Cell key={entry.name} fill={palette[index % palette.length]} />
                            ))}
                        </Pie>
                    </PieChart>
                </ResponsiveContainer>
            );
        }

        if (config.graphType === 'line') {
            const lineRows = pivotView ? pivotView.points : lineData(points);
            const lineKeys = visibleMetrics.map(v => metricKey(v.metric));
            return (
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <LineChart
                        data={lineRows}
                        margin={{
                            top: 8,
                            right: 24,
                            left: 8,
                            bottom: 8,
                        }}
                    >
                        {renderCategoryXAxis(lineRows.length)}
                        <YAxis tickFormatter={lineAxisTick} width={64} tick={{ fontSize: 11 }} />
                        <Tooltip
                            formatter={(value: number) =>
                                controls.showPercent ? labelText(value) : fmt(value)
                            }
                        />
                        <Legend />
                        {renderCategoryBrush(lineRows.length)}
                        {pivotView
                            ? pivotView.series.map((s, index) => (
                                  <Line
                                      key={s}
                                      dataKey={s}
                                      name={s}
                                      stroke={palette[index % palette.length]}
                                      strokeWidth={2}
                                      dot={false}
                                  />
                              ))
                            : visibleMetrics.map(({ metric, index }) => (
                                  <Line
                                      key={metricKey(metric)}
                                      dataKey={metricKey(metric)}
                                      name={labelOf(metric.field)}
                                      stroke={metricColor(index)}
                                      strokeWidth={2}
                                      dot={false}
                                  >
                                      {renderPointLabels(
                                          lineRows,
                                          lineKeys,
                                          metricKey(metric),
                                          expanded,
                                      )}
                                  </Line>
                              ))}
                    </LineChart>
                </ResponsiveContainer>
            );
        }

        if (config.graphType === 'rankedBar') {
            // Axis-less horizontal bars with the value printed at the end of each bar.
            // With a 2nd dimension, each bar is one "A - B" combo (top N), single colour.
            const barPoints =
                comboPoints ?? points.map(row => ({ name: String(row.name), value: valueOf(row) }));
            return (
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart
                        layout="vertical"
                        data={barPoints}
                        margin={{ top: 4, right: 48, left: 8, bottom: 4 }}
                        barCategoryGap={8}
                    >
                        <XAxis type="number" hide domain={[0, (max: number) => max * 1.25]} />
                        <YAxis
                            type="category"
                            dataKey="name"
                            width={130}
                            tick={<WrappedYAxisTick />}
                            tickLine={false}
                            axisLine={false}
                        />
                        <Tooltip
                            formatter={(value: number) => fmt(value)}
                            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                        />
                        <Bar dataKey="value" fill={barColor} radius={[4, 4, 4, 4]} barSize={16}>
                            <LabelList
                                dataKey="value"
                                position="right"
                                formatter={(value: number) => fmt(value)}
                                style={{
                                    fontSize: barLabelFontSize,
                                    fill: theme.palette.text.secondary,
                                }}
                            />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            );
        }

        // bar + horizontalBar
        const horizontal = config.graphType === 'horizontalBar';
        return (
            <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart
                    layout={horizontal ? 'vertical' : 'horizontal'}
                    data={comboPoints ?? points}
                    margin={{
                        top: 8,
                        right: 32,
                        left: horizontal ? 24 : 8,
                        bottom: 8,
                    }}
                >
                    {horizontal ? (
                        <>
                            <XAxis
                                type="number"
                                tickFormatter={formatAxisCompact}
                                tick={{ fontSize: 11 }}
                            />
                            <YAxis
                                type="category"
                                dataKey="name"
                                width={130}
                                tick={{ fontSize: 11 }}
                            />
                        </>
                    ) : (
                        <>
                            {renderCategoryXAxis((comboPoints ?? points).length)}
                            <YAxis
                                tickFormatter={formatAxisCompact}
                                width={64}
                                tick={{ fontSize: 11 }}
                            />
                        </>
                    )}
                    <Tooltip formatter={(value: number) => fmt(value)} />
                    {!horizontal && renderCategoryBrush((comboPoints ?? points).length)}
                    {!comboPoints && <Legend />}
                    {comboPoints ? (
                        <Bar dataKey="value" fill={barColor} radius={[4, 4, 4, 4]} />
                    ) : (
                        config.metrics.map((m, index) => (
                            <Bar
                                key={metricKey(m)}
                                dataKey={metricKey(m)}
                                name={labelOf(m.field)}
                                // Same stackId => the metrics layer on top of each other.
                                stackId={config.stacked ? 'stack' : undefined}
                                fill={config.metrics.length === 1 ? barColor : metricColor(index)}
                                radius={[4, 4, 4, 4]}
                            />
                        ))
                    )}
                </BarChart>
            </ResponsiveContainer>
        );
    };

    // The table's columns, which are also the CSV's: one per dimension, each metric with its
    // aggregation (a calculated field under its own name).
    const tableColumns: DashboardTableColumn[] = [
        ...config.dimensions.map(d => dimensionColumn(`${DIM_PREFIX}${d.field}`, labelOf(d.field))),
        ...config.metrics.map(m => {
            if (isCalcField(m.field)) {
                const calc = calcs.find(c => c.id === calcIdOf(m.field));
                return numberColumn(
                    metricKey(m),
                    labelOf(m.field),
                    calc?.format === 'percent' ? 'ratioPercent' : 'number',
                );
            }
            return numberColumn(
                metricKey(m),
                t('Pages.DashboardHome.custom.metricWithAggregation', {
                    field: labelOf(m.field),
                    aggregation: t(
                        `Pages.DashboardHome.custom.aggregations.${
                            m.aggregation as DashboardAggregation
                        }`,
                    ),
                }),
            );
        }),
    ];

    // Everything added up — measures recomputed on the totals rather than summed.
    const totals = useMemo(
        () =>
            totalOfRows(rows, plan.metrics, calcs, plan.calcIds) as Record<string, number | string>,
        [rows, plan, calcs],
    );

    // ---- Single value ----
    const isStat = config.graphType === 'stat';
    const statHasIcon = isStat && config.statIcon != null && config.statIcon !== 'none';
    // The same query over the period the widget compares with, for the line under its figure.
    const compareFilters = useMemo(
        () => (request && isStat ? comparisonQueryFilters(queryFilters, config.compareTo) : null),
        [request, isStat, queryFilters, config.compareTo],
    );
    const compareRequest = useMemo<DashboardReportRequest | null>(
        () =>
            request && compareFilters
                ? { ...request, filters: compareFilters as DashboardReportRequest['filters'] }
                : null,
        [request, compareFilters],
    );
    const { data: compareData } = useDashboardReport(compareRequest);
    const change = useMemo(() => {
        if (!compareRequest || !compareData) return null;
        const earlierRows = shapeWidgetRows(
            ((compareData as any)?.data ?? []) as Record<string, unknown>[],
            config,
            plan,
            calcs,
        );
        const earlier = totalOfRows(earlierRows, plan.metrics, calcs, plan.calcIds);
        const first = config.metrics[0];
        return percentChange(statFigure(totals, first), statFigure(earlier, first));
    }, [compareRequest, compareData, config, plan, calcs, totals]);
    // What the line says it is against: "vs 2025", "vs previous period", "vs 3 months earlier",
    // "vs 01-Sep-2026 – 07-Sep-2026".
    const changeLabel = useMemo(() => {
        const comparison = config.compareTo;
        const ns = 'Pages.DashboardHome.custom.stat';
        if (!comparison) return '';
        if (comparison.kind === 'previousPeriod') return t(`${ns}.vsPrevious`);
        if (comparison.kind === 'dates') {
            return t(`${ns}.vsDates`, {
                from: formatDateStringIgnoreTime({ date: parseISO(comparison.from) }),
                to: formatDateStringIgnoreTime({ date: parseISO(comparison.to) }),
            });
        }
        if (comparison.unit === 'years' && comparison.amount === 1) {
            // Named by the year the earlier period starts in.
            const from = compareFilters?.find(f => f.operator === 'gte')?.value;
            return typeof from === 'string'
                ? t(`${ns}.vsYear`, { year: from.slice(0, 4) })
                : t(`${ns}.vsLastYear`);
        }
        return t(`${ns}.vsEarlier`, {
            amount: comparison.amount,
            unit: t(`${ns}.units.${comparison.unit}${comparison.amount === 1 ? 'One' : ''}`),
        });
    }, [config.compareTo, compareFilters, t, formatDateStringIgnoreTime]);

    const renderTable = (points: Record<string, number | string>[], fill = true) => (
        <Stack sx={{ height: fill ? '100%' : undefined, minHeight: 0 }}>
            {openCampaign.notFound && (
                <Alert
                    severity="info"
                    onClose={openCampaign.clearNotFound}
                    sx={{ mb: 1, flexShrink: 0 }}
                >
                    {t('Pages.DashboardHome.custom.campaignNotFound')}
                </Alert>
            )}
            <Box sx={{ flex: fill ? 1 : undefined, minHeight: 0, position: 'relative' }}>
                <DashboardDataTable
                    columns={tableColumns}
                    rows={points}
                    totals={config.showTotals === false ? null : totals}
                    pageSize={config.pageSize ?? DASHBOARD_DEFAULT_PAGE_SIZE}
                    fill={fill}
                    canOpenRow={row => campaignLinkOfRow(row) != null}
                    onOpenRow={row => {
                        const link = campaignLinkOfRow(row);
                        if (link && !openCampaign.looking) openCampaign.open(link);
                    }}
                    openRowLabel={t('Pages.DashboardHome.custom.openCampaign')}
                />
                {openCampaign.looking && (
                    <CircularProgress
                        size={20}
                        aria-label={t('Pages.DashboardHome.custom.openingCampaign')}
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                    />
                )}
            </Box>
        </Stack>
    );

    const csvRows = () =>
        tableToCsvRows(
            tableColumns,
            chartData,
            config.showTotals === false ? null : totals,
            t('Pages.DashboardHome.custom.total'),
        );

    const renderBody = () => {
        if (isLoading) {
            return (
                <Stack alignItems="center" justifyContent="center" height="100%">
                    <CircularProgress size={28} />
                </Stack>
            );
        }
        if (rows.length === 0) {
            return (
                <Stack alignItems="center" justifyContent="center" height="100%">
                    <Typography variant="body2" color="text.secondary">
                        —
                    </Typography>
                </Stack>
            );
        }

        if (config.graphType === 'stat') {
            const [first, ...others] = config.metrics;
            return (
                <DashboardStatBody
                    value={statFigure(totals, first)}
                    format={value => statNumber(value, firstIsRatio)}
                    label={config.customTitle?.trim() || !first ? undefined : labelOf(first.field)}
                    secondary={others.map(m => {
                        const value = statFigure(totals, m);
                        return {
                            value,
                            text: statNumber(value, isRatioMetric(m)),
                            label: labelOf(m.field),
                        };
                    })}
                    style={config.statStyle}
                    size={config.statSize}
                    signColors={config.signColors}
                    change={change}
                    changeLabel={changeLabel}
                    color={barColor}
                    alignStart={statHasIcon}
                    height={bodyHeight}
                />
            );
        }

        // A table is every row, a page at a time — not the top N a chart is capped to.
        if (config.graphType === 'table') {
            return renderTable(chartData);
        }

        return renderChart(cappedData, bodyHeight);
    };

    // The live toolbar. Only for a single-dimension line chart, and only where there is room to
    // read it: at full width on the dashboard, or in the expanded breakdown.
    const hasExpectedMetric = expectedKey != null;
    const controlsAvailable = isLine && !has2Dims;
    const renderChartControls = () => (
        <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <ToggleButtonGroup
                size="small"
                value={controls.viewFilter}
                exclusive
                onChange={(_, value: DashboardViewFilter | null) =>
                    value && setControl('viewFilter', value)
                }
                sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1, fontSize: 11 } }}
            >
                <ToggleButton value="both">Both</ToggleButton>
                <ToggleButton
                    value="expected"
                    disabled={!hasExpectedMetric || controls.showPercent}
                >
                    {isAudienceField(expectedMetric?.field ?? '') ? 'Audited' : 'Expected'}
                </ToggleButton>
                <ToggleButton value="delivered">Delivered</ToggleButton>
            </ToggleButtonGroup>
            <MuiTooltip title="Plot each series as a running total">
                <span>
                    <ToggleButton
                        size="small"
                        value="cumulative"
                        selected={controls.cumulative}
                        onChange={() => setControl('cumulative', !controls.cumulative)}
                        sx={{ py: 0.25, px: 1, fontSize: 11 }}
                    >
                        Accumulate
                    </ToggleButton>
                </span>
            </MuiTooltip>
            <MuiTooltip
                title={
                    hasExpectedMetric
                        ? 'Show delivered as a percentage of expected'
                        : 'Needs an expected metric on this widget'
                }
            >
                <span>
                    <ToggleButton
                        size="small"
                        value="percent"
                        selected={controls.showPercent}
                        disabled={!hasExpectedMetric}
                        onChange={() => setControl('showPercent', !controls.showPercent)}
                        sx={{ py: 0.25, px: 1, fontSize: 11 }}
                    >
                        %
                    </ToggleButton>
                </span>
            </MuiTooltip>
            <MuiTooltip title="Label every plotted point">
                <span>
                    <ToggleButton
                        size="small"
                        value="numbers"
                        selected={controls.showNumbers}
                        onChange={() => setControl('showNumbers', !controls.showNumbers)}
                        sx={{ py: 0.25, px: 1, fontSize: 11 }}
                    >
                        Numbers
                    </ToggleButton>
                </span>
            </MuiTooltip>
        </Stack>
    );

    // ---- Size ----
    // On the grid the body is whatever is left of the box; the charts need that as a number.
    const bodyRef = useRef<HTMLDivElement>(null);
    const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);
    useEffect(() => {
        const el = bodyRef.current;
        if (!fill || !el || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(([entry]) =>
            setMeasuredHeight(Math.floor(entry.contentRect.height)),
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [fill]);
    const bodyHeight = fill && measuredHeight != null ? Math.max(measuredHeight, 60) : height;

    // ---- Downloads ----
    const cardRef = useRef<HTMLDivElement>(null);
    const [downloadAnchor, setDownloadAnchor] = useState<HTMLElement | null>(null);
    const [downloading, setDownloading] = useState(false);
    const fileName = exportFileName(displayTitle);
    const downloadImage = (kind: 'png' | 'pdf') => {
        setDownloadAnchor(null);
        if (!cardRef.current) return;
        setDownloading(true);
        downloadElementImage(cardRef.current, fileName, kind)
            .finally(() => setDownloading(false))
            .catch(() => undefined);
    };
    const downloadData = () => {
        setDownloadAnchor(null);
        downloadCsv(csvRows(), fileName);
    };
    const canDownload = !previewData && !isLoading && rows.length > 0;
    // The dashboard's "all data" download asks each widget for this.
    useRegisterWidgetExport(config.id, () =>
        canDownload ? { title: displayTitle, rows: csvRows() } : null,
    );

    // ---- Full breakdown ----
    // Tables, pies and donuts break down into the full table; a bar chart can show either, a
    // page of categories at a time, so a long ranking stays readable.
    const [breakdownView, setBreakdownView] = useState<'chart' | 'table'>('chart');
    const [breakdownPage, setBreakdownPage] = useState(0);
    useEffect(() => setBreakdownPage(0), [breakdownOpen, chartData]);
    const breakdownTableOnly =
        config.graphType === 'table' || config.graphType === 'donut' || config.graphType === 'pie';
    const horizontalChart =
        config.graphType === 'horizontalBar' || config.graphType === 'rankedBar';
    // A trend or a series line runs along its axis; paging it would cut the line up.
    const breakdownPaged = !isTrend && !isSeriesLine && config.graphType !== 'line';
    const pageStart = breakdownPage * BREAKDOWN_PAGE_SIZE;
    const pageEnd = pageStart + BREAKDOWN_PAGE_SIZE;
    const renderBreakdownChart = () => {
        if (!breakdownPaged) {
            return renderChart(
                chartData,
                horizontalChart ? Math.max(fullCount * 34, 320) : 420,
                true,
            );
        }
        const count = Math.min(fullCount - pageStart, BREAKDOWN_PAGE_SIZE);
        return renderChart(
            chartData.slice(pageStart, pageEnd),
            horizontalChart ? Math.max(count * 34, 320) : 420,
            true,
            comboData ? [pageStart, pageEnd] : undefined,
        );
    };

    return (
        <Card
            ref={cardRef}
            sx={{
                borderRadius: 3,
                height: '100%',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                ...(fill ? { display: 'flex', flexDirection: 'column' } : {}),
                ...(editing ? { outline: th => `1px dashed ${th.palette.primary.main}` } : {}),
            }}
        >
            <CardContent
                sx={{
                    p: 2,
                    '&:last-child': { pb: 2 },
                    ...(fill
                        ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
                        : {}),
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                    className={editing ? DASHBOARD_DRAG_HANDLE_CLASS : undefined}
                    sx={{ mb: 1.5, flexShrink: 0, ...(editing ? { cursor: 'move' } : {}) }}
                >
                    <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                        {editing && (
                            <DragIndicator
                                fontSize="small"
                                color="action"
                                aria-label={t('Pages.DashboardHome.custom.layout.dragHandle')}
                            />
                        )}
                        <DashboardStatIconTile
                            icon={isStat ? config.statIcon : undefined}
                            color={barColor}
                        />
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                overflowWrap: 'anywhere',
                                ...(statHasIcon ? { ml: 0.5 } : {}),
                            }}
                        >
                            {displayTitle}
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        {controlsAvailable && widthPct >= 100 && renderChartControls()}
                        {canDownload && (
                            <Box {...{ [EXPORT_IGNORE_ATTR]: true }}>
                                <MuiTooltip title={t('Pages.DashboardHome.custom.download')}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            aria-label={t('Pages.DashboardHome.custom.download')}
                                            disabled={downloading}
                                            onClick={e => setDownloadAnchor(e.currentTarget)}
                                        >
                                            {downloading ? (
                                                <CircularProgress size={16} />
                                            ) : (
                                                <Download fontSize="small" />
                                            )}
                                        </IconButton>
                                    </span>
                                </MuiTooltip>
                                <Menu
                                    anchorEl={downloadAnchor}
                                    open={downloadAnchor != null}
                                    onClose={() => setDownloadAnchor(null)}
                                >
                                    <MenuItem onClick={() => downloadImage('png')}>
                                        <ListItemIcon>
                                            <Image fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>
                                            {t('Pages.DashboardHome.custom.downloadPng')}
                                        </ListItemText>
                                    </MenuItem>
                                    <MenuItem onClick={() => downloadImage('pdf')}>
                                        <ListItemIcon>
                                            <PictureAsPdf fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>
                                            {t('Pages.DashboardHome.custom.downloadPdf')}
                                        </ListItemText>
                                    </MenuItem>
                                    <MenuItem onClick={downloadData}>
                                        <ListItemIcon>
                                            <TableChart fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>
                                            {t('Pages.DashboardHome.custom.downloadCsv')}
                                        </ListItemText>
                                    </MenuItem>
                                </Menu>
                            </Box>
                        )}
                    </Stack>
                </Stack>
                <Box ref={bodyRef} sx={fill ? { flex: 1, minHeight: 0 } : { height }}>
                    {renderBody()}
                </Box>
                {hasMore &&
                    config.graphType !== 'stat' &&
                    config.graphType !== 'deliveryComparison' && (
                        <Box
                            {...{ [EXPORT_IGNORE_ATTR]: true }}
                            sx={{
                                mt: 1,
                                pt: 1,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                textAlign: 'center',
                            }}
                        >
                            <Button size="small" onClick={() => setBreakdownOpen(true)}>
                                View full breakdown
                            </Button>
                        </Box>
                    )}
            </CardContent>

            <DialogV2
                open={breakdownOpen}
                onClose={() => setBreakdownOpen(false)}
                title={breakdownTitle}
                maxWidth="xl"
            >
                <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ pb: 1 }}>
                    {!breakdownTableOnly && (
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={breakdownView}
                            onChange={(_, v: 'chart' | 'table' | null) => v && setBreakdownView(v)}
                            sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1, fontSize: 11 } }}
                        >
                            <ToggleButton value="chart">
                                {t('Pages.DashboardHome.custom.viewChart')}
                            </ToggleButton>
                            <ToggleButton value="table">
                                {t('Pages.DashboardHome.custom.viewTable')}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}
                    {controlsAvailable &&
                        !breakdownTableOnly &&
                        breakdownView === 'chart' &&
                        renderChartControls()}
                    <MuiTooltip title={t('Pages.DashboardHome.custom.downloadCsv')}>
                        <IconButton
                            size="small"
                            aria-label={t('Pages.DashboardHome.custom.downloadCsv')}
                            onClick={downloadData}
                        >
                            <Download fontSize="small" />
                        </IconButton>
                    </MuiTooltip>
                </Stack>
                {breakdownTableOnly || breakdownView === 'table' ? (
                    <Box sx={{ height: '65vh' }}>{renderTable(chartData)}</Box>
                ) : (
                    <>
                        <Box sx={{ maxHeight: '65vh', overflowY: 'auto' }}>
                            {renderBreakdownChart()}
                        </Box>
                        {breakdownPaged && fullCount > BREAKDOWN_PAGE_SIZE && (
                            <TablePagination
                                component="div"
                                count={fullCount}
                                page={breakdownPage}
                                rowsPerPage={BREAKDOWN_PAGE_SIZE}
                                rowsPerPageOptions={[BREAKDOWN_PAGE_SIZE]}
                                onPageChange={(_, next) => setBreakdownPage(next)}
                                labelDisplayedRows={({ from, to, count }) =>
                                    t('Pages.DashboardHome.custom.displayedRows', {
                                        from,
                                        to,
                                        count,
                                    })
                                }
                            />
                        )}
                    </>
                )}
            </DialogV2>
        </Card>
    );
};

export default DashboardWidget;
